import {AlertTriangleIcon, InfoCircleIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {GenerateNrAbsencesResponse} from '@/features/accesses/types'
import {
    StyledGenerateNrAbsencesInfoBox,
    StyledGenerateNrAbsencesPlanList,
    StyledGenerateNrAbsencesPlanRow,
    StyledGenerateNrAbsencesPlanScroller,
    StyledGenerateNrAbsencesSummary,
    StyledGenerateNrAbsencesToCreate,
    StyledGenerateNrAbsencesWarningBox
} from './style'

type GenerateNrAbsencesPlanStepProps = {
    plan: GenerateNrAbsencesResponse
    totalToCreate: number
    totalMissingCapacity: number
}

export const GenerateNrAbsencesPlanStep = ({
    plan,
    totalToCreate,
    totalMissingCapacity
}: GenerateNrAbsencesPlanStepProps) => {
    const {t} = useTranslation()
    const projectsToCreate = plan.projects.filter(project => project.toCreate > 0)

    return (
        <>
            <StyledGenerateNrAbsencesInfoBox>
                <InfoCircleIcon size={16} />
                {t('accesses:generate_nr_absences_description')}
            </StyledGenerateNrAbsencesInfoBox>

            <StyledGenerateNrAbsencesSummary>
                <Flexbox direction="column" gap={0.5}>
                    <Text size="textXs" palette="neutral" shade="500">
                        {t('accesses:generate_nr_absences_projects_involved')}
                    </Text>
                    <Text size="textLg" weight={500} palette="neutral" shade="950">
                        {projectsToCreate.length}
                    </Text>
                </Flexbox>
                <Flexbox direction="column" gap={0.5}>
                    <Text size="textXs" palette="neutral" shade="500">
                        {t('accesses:generate_nr_absences_absences_to_create')}
                    </Text>
                    <Text size="textLg" weight={500} palette="neutral" shade="950">
                        {totalToCreate}
                    </Text>
                </Flexbox>
            </StyledGenerateNrAbsencesSummary>

            {projectsToCreate.length > 0 && (
                <StyledGenerateNrAbsencesPlanList>
                    <StyledGenerateNrAbsencesPlanRow $isHeader>
                        <span>{t('commons:pri_n')}</span>
                        <span>{t('accesses:expected')}</span>
                        <span>{t('accesses:completed')}</span>
                        <span>{t('accesses:generate_nr_absences_column_to_create')}</span>
                    </StyledGenerateNrAbsencesPlanRow>
                    <StyledGenerateNrAbsencesPlanScroller>
                        {projectsToCreate.map(project => (
                            <StyledGenerateNrAbsencesPlanRow key={project.projectId}>
                                <span>{project.projectCode ?? '-'}</span>
                                <span>{project.planned}</span>
                                <span>{project.done}</span>
                                <StyledGenerateNrAbsencesToCreate>{project.toCreate}</StyledGenerateNrAbsencesToCreate>
                            </StyledGenerateNrAbsencesPlanRow>
                        ))}
                    </StyledGenerateNrAbsencesPlanScroller>
                </StyledGenerateNrAbsencesPlanList>
            )}

            {totalMissingCapacity > 0 && (
                <StyledGenerateNrAbsencesWarningBox>
                    <AlertTriangleIcon size={16} />
                    {t('accesses:generate_nr_absences_missing_capacity', {count: totalMissingCapacity})}
                </StyledGenerateNrAbsencesWarningBox>
            )}
        </>
    )
}
